import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { format } from 'date-fns'
import TableComponent from '../../../components/Table/TableComponent'
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker'
import {
  TableRow, TableCell
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear';
import Chart from '../../../components/Chart'
import SearchIcon from '@mui/icons-material/Search'

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config'
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import ChartButton from '../../../components/Button/ChartButton'
import TablePaginationComponent from '../../../components/TablePaginationComponent'
import { ObjectiveResponse, Urea } from '../../../components/interface/ObjectiveInterface'

const UreaForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [ureaRecords, setUreaRecords] = useState<Urea[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [openDialog, setOpenDialog] = useState(false)
  const [editRecord, setEditRecord] = useState<Urea | null>(null)
  const [showChart, setShowChart] = useState(false)
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success')
  const [filtersApplied, setFiltersApplied] = useState(false);



  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  useEffect(() => {

    fetchUreaRecords(dateRange.startDate, dateRange.endDate)
  }, [page, rowsPerPage, searchTerm])


  const fetchUreaRecords = async (startDate: string = '', endDate: string = '') => {
    setUreaRecords([])
    setLoading(true)
    try {
      const response = await axios.get<ObjectiveResponse>(
        `${BASE_URL}/resource/vitals`,
        {
          params: {
            limit: rowsPerPage,
            page: page + 1,
            from: startDate || '',
            to: endDate || '',
            order_by: 'details->date',
            dir: 2,
            user_id: patientId,
            slug: 'urea',
            searchkey: searchTerm || undefined
          },
          headers: getAxiosConfig().headers
        }
      )

      const { vitals } = response.data.data;
      const total = response.data.data.pagination!.total;

      if (Array.isArray(vitals) && vitals.length > 0) {
        const formattedData: Urea[] = vitals.map((vital) => (
          {
            id: vital.id,
            user_id: vital.user_id, // Add user_id
            consult_id: vital.consult_id, // Add consult_id
            peripheral_id: vital.peripheral_id, // Add peripheral_id
            slug: vital.slug, // Add slug
            details: {
              date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
              time: vital.details.time,
              urea: vital.details.urea,
              unit: vital.details.unit,
              ureaFlag: vital.details.ureaFlag,
              range_code: vital.details.range_code,
              ureaFlagColor: vital.details.ureaFlagColor,
            },
            freeze: vital.freeze,
            document: vital.document,
          }));
        setUreaRecords(formattedData); // Set the formatted data to state
        setTotalRecords(total);

      }
    } catch (error) {
      console.error('Error fetching urea records:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    setSearchTerm('')
    setDateRange({ startDate: '', endDate: '' })
    setPage(0)
    setRowsPerPage(5);
    fetchUreaRecords()
    setFiltersApplied(false)
  };

  const renderRow = (item: Urea, index: number) => {
    console.log('Rendering row:', item)
    return (
      <TableRow key={index}>
        <TableCell className="px-4 py-3  font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{page * rowsPerPage + index + 1}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.date}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.time}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.urea} </TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
          <span
            style={{
              backgroundColor: item.details.range_code,
              padding: '4px 8px',
              borderRadius: '8px',
              color: '#FFFFFF'
            }}
          >
            {item.details.ureaFlag}
          </span>
        </TableCell>
      </TableRow>
    )
  }

  const chartData = {
    labels: ureaRecords.map((record) => record.details.date),
    datasets: [
      {
        label: 'Urea Level (mg/dL)',
        // Convert the urea values to numbers
        data: ureaRecords.map((record) => Number(record.details.urea)), // Ensure this is a number[]
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.raw} mg/dL`
        }
      }
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between w-full gap-4 pb-4">
        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker
            startDateId={'Urea_Start_Date'}
            endDateId={'Urea_End_Date'}
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates);
              setFiltersApplied(true);
            }}
            value={dateRange}
          />
          <div className="flex items-center gap-2 shrink-0">
            <IconButton
              icon={<SearchIcon />}
              onClick={() => {
                fetchUreaRecords(dateRange.startDate, dateRange.endDate);
                setFiltersApplied(true);
              }}
            />
            {filtersApplied && (
              <IconButton
                icon={<ClearIcon />}
                onClick={handleClearFilters}
              />
            )}
          </div>
        </div>
        <div className="flex items-center shrink-0">
          {ureaRecords.length > 0 && (
            <ChartButton text={showChart ? 'List' : 'Chart'} onClick={() => setShowChart(prev => !prev)} />
          )}
        </div>
      </div>

      {loading ? (

        <div className="text-center my-4">
         
        </div>
      ) : (
        <>
          {showChart ? (
            <Chart data={chartData} options={chartOptions} />
          ) : (
            <div>
              <TableComponent
                headers={['S.No', 'Date', 'Time', 'Value (mg/dL)', 'Result']}
                data={ureaRecords}
                renderRow={renderRow} error={''}
              />
              
            </div>
          )}
        </>
      )}

      <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage)
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10)
          setRowsPerPage(newRowsPerPage)
          setPage(0)
        }} />
   
    </div>
  )
}

export default UreaForm

